import {
  Color,
  PaletteMode,
  PaletteOptions,
  Theme,
  createTheme,
} from '@mui/material';

// Enums via the `enums` sub-barrel, not top `#shared` (it re-exports theme/), to keep shared/ acyclic.
import {
  EAircraftStatus,
  EMaoMaintenanceBadge,
  ENOPOrganType,
  ESeverity,
} from '../enums';

export type TGetPaletteOptions = {
  mode: PaletteMode;
  background: string;
  primary: string;
  grey: Color;
  blue: Color;
  mapBlue: Color;
  green: Color;
  violet: Color;
  orange: Color;
  mint: Color;
  yellow: Color;
  /** Surface colour of Paper-based components; the mode's MUI default when omitted. */
  paper?: string;
};
export const getPalette = ({
  mode,
  background,
  primary,
  grey,
  blue,
  mapBlue,
  green,
  violet,
  orange,
  mint,
  yellow,
  paper,
}: TGetPaletteOptions): PaletteOptions => {
  // A theme merged over another keeps the base's text, action and divider colours, so each mode sets its own (FE ARCH-016).
  const {
    text,
    action,
    divider,
    background: modeBackground,
  } = createTheme({
    palette: { mode, blue, mapBlue, green, violet, orange, mint, yellow },
  }).palette;

  return {
    mode,
    text,
    action,
    divider,
    background: {
      default: background,
      paper: paper ?? modeBackground.paper,
    },
    primary: {
      main: primary,
    },
    grey,
    blue,
    mapBlue,
    green,
    violet,
    orange,
    mint,
    yellow,
  };
};

export const getOpacityHex = (opacity: number): string =>
  Math.min(Math.max(Math.round(opacity * 255), 0), 255).toString(16);

const opacity = getOpacityHex(0.15);
export const getSeverityStyles = ({ palette }: Theme) => ({
  [`.severity-status-${ESeverity.CRITICAL}`]: {
    ['--primaryColor']: palette.severity.critical,
    ['--secondaryColor']: `${palette.severity.critical}${opacity}`,
  },
  [`.severity-status-${ESeverity.MAJOR}`]: {
    ['--primaryColor']: palette.severity.major,
    ['--secondaryColor']: `${palette.severity.major}${opacity}`,
  },
  [`.severity-status-${ESeverity.MINOR}`]: {
    ['--primaryColor']: palette.severity.minor,
    ['--secondaryColor']: `${palette.severity.minor}${opacity}`,
  },
  [`.severity-status-${ESeverity.LOW}`]: {
    ['--primaryColor']: palette.severity.low,
    ['--secondaryColor']: `${palette.severity.low}${opacity}`,
  },
});

export const getAircraftStatusStyles = ({ palette }: Theme) => ({
  [`.status-${EAircraftStatus.NotAvailable}`]: {
    ['--primaryColor']: palette.aircraftStatus.notAvailable,
    ['--secondaryColor']: `${palette.aircraftStatus.notAvailable}${opacity}`,
  },
  [`.aircraft-status-${EAircraftStatus.Available}`]: {
    ['--primaryColor']: palette.aircraftStatus.available,
    ['--secondaryColor']: `${palette.aircraftStatus.available}${opacity}`,
  },
  [`.aircraft-status-${EAircraftStatus.Maintenance}`]: {
    ['--primaryColor']: palette.aircraftStatus.maintenance,
    ['--secondaryColor']: `${palette.aircraftStatus.maintenance}${opacity}`,
    [`&.maintenance-type-${EMaoMaintenanceBadge.AOG}`]: {
      ['--primaryColor']: palette.aircraftStatus.aog,
      ['--secondaryColor']: `${palette.aircraftStatus.aog}${opacity}`,
    },
  },
});
export const getOrganStyles = ({ palette }: Theme) => ({
  [`.organ-type-${ENOPOrganType.Heart}`]: {
    ['--primaryColor']: palette.organ.heart,
    ['--secondaryColor']: `${palette.organ.heart}${opacity}`,
  },
  [`.organ-type-${ENOPOrganType.Liver}`]: {
    ['--primaryColor']: palette.organ.liver,
    ['--secondaryColor']: `${palette.organ.liver}${opacity}`,
  },
  [`.organ-type-${ENOPOrganType.Lungs}`]: {
    ['--primaryColor']: palette.organ.lungs,
    ['--secondaryColor']: `${palette.organ.lungs}${opacity}`,
  },
});
